import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  HiOutlineArrowLeft, HiOutlineRefresh, HiOutlineInformationCircle,
  HiOutlineDocumentText, HiOutlineChartBar, HiOutlineFilter,
  HiOutlineClock, HiOutlineChevronDown
} from 'react-icons/hi';
import { getCloudRunInfo, getCloudRunLogs, getCloudRunMetrics } from '../services/api';
import './CloudRunDetail.css';

const SEVERITY_LEVELS = ['DEFAULT', 'DEBUG', 'INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'];

const TIME_PRESETS = [
  { label: 'Last 1 hour', value: '1h', hours: 1 },
  { label: 'Last 6 hours', value: '6h', hours: 6 },
  { label: 'Last 12 hours', value: '12h', hours: 12 },
  { label: 'Last 24 hours', value: '1d', hours: 24 },
  { label: 'Last 7 days', value: '7d', hours: 168 },
  { label: 'Last 30 days', value: '30d', hours: 720 },
  { label: 'Last 90 days', value: '90d', hours: 2160 },
  { label: 'Last 6 months', value: '6mo', hours: 4320 },
  { label: 'Custom range', value: 'custom', hours: 0 },
];

function getPresetRange(hours) {
  const now = new Date();
  return {
    startTime: new Date(now.getTime() - hours * 60 * 60 * 1000).toISOString(),
    endTime: now.toISOString(),
  };
}

function toLocalDatetimeString(isoString) {
  const d = new Date(isoString);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function CloudRunDetail() {
  const { projectId, region, serviceName } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('info');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsNextPageToken, setLogsNextPageToken] = useState(null);
  const [loadingMoreLogs, setLoadingMoreLogs] = useState(false);
  const [logTimePreset, setLogTimePreset] = useState('1h');
  const [logStartTime, setLogStartTime] = useState('');
  const [logEndTime, setLogEndTime] = useState('');
  const [logSeverityFilter, setLogSeverityFilter] = useState([]);
  const [showSeverityDropdown, setShowSeverityDropdown] = useState(false);
  const [showTimePresets, setShowTimePresets] = useState(false);
  const [logTotalCount, setLogTotalCount] = useState(0);

  // Metrics state
  const [metrics, setMetrics] = useState(null);
  const [metricsTimeRange, setMetricsTimeRange] = useState(null);
  const [metricsTimePreset, setMetricsTimePreset] = useState('1h');
  const [metricsStartTime, setMetricsStartTime] = useState('');
  const [metricsEndTime, setMetricsEndTime] = useState('');
  const [showMetricsTimePresets, setShowMetricsTimePresets] = useState(false);

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCloudRunInfo(projectId, serviceName, region);
      setInfo(res.data);
    } catch (err) {
      toast.error('Failed to fetch service info');
    }
    setLoading(false);
  }, [projectId, serviceName, region]);

  const fetchLogs = useCallback(async (append = false) => {
    if (append) {
      setLoadingMoreLogs(true);
    } else {
      setLoading(true);
    }
    try {
      let startTime, endTime;
      if (logTimePreset === 'custom') {
        startTime = logStartTime ? new Date(logStartTime).toISOString() : undefined;
        endTime = logEndTime ? new Date(logEndTime).toISOString() : undefined;
      } else {
        const preset = TIME_PRESETS.find(p => p.value === logTimePreset);
        if (preset) {
          const range = getPresetRange(preset.hours);
          startTime = range.startTime;
          endTime = range.endTime;
        }
      }

      const res = await getCloudRunLogs(projectId, serviceName, region, {
        startTime,
        endTime,
        severity: logSeverityFilter,
      });

      if (append) {
        setLogs(prev => [...prev, ...res.data.logs]);
      } else {
        setLogs(res.data.logs);
      }
      setLogsNextPageToken(res.data.nextPageToken);
      setLogTotalCount(append ? logs.length + res.data.logs.length : res.data.logs.length);
    } catch (err) {
      toast.error('Failed to fetch logs');
    }
    setLoading(false);
    setLoadingMoreLogs(false);
  }, [projectId, serviceName, region, logTimePreset, logStartTime, logEndTime, logSeverityFilter, logs.length]);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      let startTime, endTime;
      if (metricsTimePreset === 'custom') {
        startTime = metricsStartTime ? new Date(metricsStartTime).toISOString() : undefined;
        endTime = metricsEndTime ? new Date(metricsEndTime).toISOString() : undefined;
      } else {
        const preset = TIME_PRESETS.find(p => p.value === metricsTimePreset);
        if (preset) {
          const range = getPresetRange(preset.hours);
          startTime = range.startTime;
          endTime = range.endTime;
        }
      }

      const res = await getCloudRunMetrics(projectId, serviceName, region, {
        startTime,
        endTime,
      });
      setMetrics(res.data.metrics);
      setMetricsTimeRange(res.data.timeRange);
    } catch (err) {
      toast.error('Failed to fetch metrics');
    }
    setLoading(false);
  }, [projectId, serviceName, region, metricsTimePreset, metricsStartTime, metricsEndTime]);

  useEffect(() => {
    if (activeTab === 'info') fetchInfo();
  }, [activeTab, fetchInfo]);

  useEffect(() => {
    if (activeTab === 'logs') fetchLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, logTimePreset, logSeverityFilter]);

  useEffect(() => {
    if (activeTab === 'metrics') fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, metricsTimePreset]);

  const handleRefresh = () => {
    if (activeTab === 'info') fetchInfo();
    else if (activeTab === 'logs') fetchLogs(false);
    else if (activeTab === 'metrics') fetchMetrics();
  };

  const handleLogCustomApply = () => {
    if (logStartTime && logEndTime) {
      fetchLogs(false);
    } else {
      toast.error('Both start and end times are required');
    }
  };

  const handleMetricsCustomApply = () => {
    if (metricsStartTime && metricsEndTime) {
      fetchMetrics();
    } else {
      toast.error('Both start and end times are required');
    }
  };

  const toggleSeverity = (sev) => {
    setLogSeverityFilter(prev =>
      prev.includes(sev) ? prev.filter(s => s !== sev) : [...prev, sev]
    );
  };

  const clearSeverityFilter = () => setLogSeverityFilter([]);

  const getSeverityClass = (severity) => {
    const map = {
      EMERGENCY: 'severity-emergency',
      ALERT: 'severity-alert',
      CRITICAL: 'severity-critical',
      ERROR: 'severity-error',
      WARNING: 'severity-warning',
      NOTICE: 'severity-notice',
      INFO: 'severity-info',
      DEBUG: 'severity-debug',
    };
    return map[severity] || 'severity-default';
  };

  const activeLogPresetLabel = TIME_PRESETS.find(p => p.value === logTimePreset)?.label || 'Select time';
  const activeMetricsPresetLabel = TIME_PRESETS.find(p => p.value === metricsTimePreset)?.label || 'Select time';

  return (
    <div className="cloud-run-detail" onClick={() => { setShowSeverityDropdown(false); setShowTimePresets(false); setShowMetricsTimePresets(false); }}>
      <div className="detail-header">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
          <HiOutlineArrowLeft size={16} /> Back
        </button>
        <div className="detail-title">
          <h1>{serviceName}</h1>
          <div className="detail-meta">
            <span className="meta-item">{region}</span>
            <span className="status-badge healthy">
              <span className="status-dot"></span> Active
            </span>
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={handleRefresh}>
          <HiOutlineRefresh size={16} /> Refresh
        </button>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
          <HiOutlineInformationCircle size={16} /> Info
        </button>
        <button className={`tab ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>
          <HiOutlineDocumentText size={16} /> Logs
        </button>
        <button className={`tab ${activeTab === 'metrics' ? 'active' : ''}`} onClick={() => setActiveTab('metrics')}>
          <HiOutlineChartBar size={16} /> Metrics
        </button>
      </div>

      {loading && (
        <div className="loading-spinner"><div className="spinner"></div></div>
      )}

      {/* ========== INFO TAB ========== */}
      {!loading && activeTab === 'info' && info && (
        <div className="info-section">
          <div className="info-grid">
            <InfoItem label="Service Name" value={info.name} />
            <InfoItem label="Region" value={info.region} />
            <InfoItem label="URL" value={info.uri} link />
            <InfoItem label="Created" value={formatDate(info.createTime)} />
            <InfoItem label="Last Updated" value={formatDate(info.updateTime)} />
            <InfoItem label="Creator" value={info.creator} />
            <InfoItem label="Last Modifier" value={info.lastModifier} />
            <InfoItem label="Ingress" value={info.ingress} />
            <InfoItem label="Latest Ready Revision" value={info.latestReadyRevision?.split('/').pop()} />
            <InfoItem label="Latest Created Revision" value={info.latestCreatedRevision?.split('/').pop()} />
          </div>
          {info.template && (
            <>
              <h3 className="subsection-title">Container Configuration</h3>
              <div className="info-grid">
                {info.template.containers?.map((container, i) => (
                  <React.Fragment key={i}>
                    <InfoItem label="Image" value={container.image} />
                    <InfoItem
                      label="Resources"
                      value={container.resources?.limits ? `CPU: ${container.resources.limits.cpu || 'N/A'}, Memory: ${container.resources.limits.memory || 'N/A'}` : 'Default'}
                    />
                    <InfoItem label="Ports" value={container.ports?.map(p => p.containerPort).join(', ') || 'N/A'} />
                  </React.Fragment>
                ))}
                <InfoItem
                  label="Scaling"
                  value={info.template.scaling ? `Min: ${info.template.scaling.minInstanceCount || 0}, Max: ${info.template.scaling.maxInstanceCount || 'auto'}` : 'Default'}
                />
                <InfoItem label="Timeout" value={info.template.timeout ? `${info.template.timeout.seconds}s` : 'Default'} />
                <InfoItem label="Concurrency" value={info.template.maxInstanceRequestConcurrency || 'Default'} />
                <InfoItem label="Service Account" value={info.template.serviceAccount} />
              </div>
            </>
          )}
          {info.conditions && info.conditions.length > 0 && (
            <>
              <h3 className="subsection-title">Conditions</h3>
              <div className="conditions-table">
                <table>
                  <thead>
                    <tr><th>Type</th><th>State</th><th>Message</th><th>Last Transition</th></tr>
                  </thead>
                  <tbody>
                    {info.conditions.map((c, i) => (
                      <tr key={i}>
                        <td>{c.type}</td>
                        <td><span className={`condition-state ${c.state === 'CONDITION_SUCCEEDED' ? 'success' : ''}`}>{c.state}</span></td>
                        <td>{c.message || '-'}</td>
                        <td>{formatDate(c.lastTransitionTime)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ========== LOGS TAB ========== */}
      {!loading && activeTab === 'logs' && (
        <div className="logs-section">
          <div className="filter-toolbar">
            {/* Time Range Picker */}
            <div className="filter-group" onClick={(e) => e.stopPropagation()}>
              <div className="dropdown-trigger" onClick={() => { setShowTimePresets(!showTimePresets); setShowSeverityDropdown(false); }}>
                <HiOutlineClock size={15} />
                <span>{activeLogPresetLabel}</span>
                <HiOutlineChevronDown size={14} />
              </div>
              {showTimePresets && (
                <div className="dropdown-menu time-dropdown">
                  {TIME_PRESETS.map(preset => (
                    <div
                      key={preset.value}
                      className={`dropdown-item ${logTimePreset === preset.value ? 'active' : ''}`}
                      onClick={() => { setLogTimePreset(preset.value); if (preset.value !== 'custom') setShowTimePresets(false); }}
                    >
                      {preset.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Severity Filter */}
            <div className="filter-group" onClick={(e) => e.stopPropagation()}>
              <div className="dropdown-trigger" onClick={() => { setShowSeverityDropdown(!showSeverityDropdown); setShowTimePresets(false); }}>
                <HiOutlineFilter size={15} />
                <span>
                  {logSeverityFilter.length === 0 ? 'All severities' : `${logSeverityFilter.length} selected`}
                </span>
                <HiOutlineChevronDown size={14} />
              </div>
              {showSeverityDropdown && (
                <div className="dropdown-menu severity-dropdown">
                  <div className="dropdown-header">
                    <span>Filter by severity</span>
                    {logSeverityFilter.length > 0 && (
                      <button className="clear-filter-btn" onClick={clearSeverityFilter}>Clear</button>
                    )}
                  </div>
                  {SEVERITY_LEVELS.map(sev => (
                    <label key={sev} className={`dropdown-item checkbox-item ${logSeverityFilter.includes(sev) ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={logSeverityFilter.includes(sev)}
                        onChange={() => toggleSeverity(sev)}
                      />
                      <span className={`severity-dot ${getSeverityClass(sev)}`}></span>
                      <span>{sev}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Active severity chips */}
            {logSeverityFilter.length > 0 && (
              <div className="active-filters">
                {logSeverityFilter.map(sev => (
                  <span key={sev} className={`filter-chip ${getSeverityClass(sev)}`}>
                    {sev}
                    <button onClick={() => toggleSeverity(sev)}>&times;</button>
                  </span>
                ))}
              </div>
            )}

            <div className="filter-spacer"></div>

            <span className="log-count">{logTotalCount} entries</span>
          </div>

          {/* Custom time range inputs */}
          {logTimePreset === 'custom' && (
            <div className="custom-time-range">
              <div className="time-input-group">
                <label>Start Time</label>
                <input
                  type="datetime-local"
                  value={logStartTime ? toLocalDatetimeString(logStartTime) : ''}
                  onChange={(e) => setLogStartTime(e.target.value ? new Date(e.target.value).toISOString() : '')}
                  max={toLocalDatetimeString(new Date().toISOString())}
                />
              </div>
              <div className="time-input-group">
                <label>End Time</label>
                <input
                  type="datetime-local"
                  value={logEndTime ? toLocalDatetimeString(logEndTime) : ''}
                  onChange={(e) => setLogEndTime(e.target.value ? new Date(e.target.value).toISOString() : '')}
                  max={toLocalDatetimeString(new Date().toISOString())}
                />
              </div>
              <button className="btn btn-primary btn-sm" onClick={handleLogCustomApply}>
                Apply
              </button>
            </div>
          )}

          {/* Logs list */}
          {logs.length === 0 ? (
            <div className="empty-state">
              <h3>No logs found</h3>
              <p>No log entries match the selected filters and time range</p>
            </div>
          ) : (
            <>
              <div className="logs-table">
                <div className="logs-table-header">
                  <div className="log-col-severity">Severity</div>
                  <div className="log-col-timestamp">Timestamp</div>
                  <div className="log-col-content">Summary</div>
                </div>
                <div className="logs-table-body">
                  {logs.map((log, i) => (
                    <LogRow key={`${log.insertId}-${i}`} log={log} getSeverityClass={getSeverityClass} />
                  ))}
                </div>
              </div>

              {logsNextPageToken && (
                <div className="load-more-container">
                  <button
                    className="btn btn-secondary"
                    onClick={() => fetchLogs(true)}
                    disabled={loadingMoreLogs}
                  >
                    {loadingMoreLogs ? 'Loading...' : 'Load more logs'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ========== METRICS TAB ========== */}
      {!loading && activeTab === 'metrics' && (
        <div className="metrics-section">
          <div className="filter-toolbar">
            <div className="filter-group" onClick={(e) => e.stopPropagation()}>
              <div className="dropdown-trigger" onClick={() => { setShowMetricsTimePresets(!showMetricsTimePresets); }}>
                <HiOutlineClock size={15} />
                <span>{activeMetricsPresetLabel}</span>
                <HiOutlineChevronDown size={14} />
              </div>
              {showMetricsTimePresets && (
                <div className="dropdown-menu time-dropdown">
                  {TIME_PRESETS.map(preset => (
                    <div
                      key={preset.value}
                      className={`dropdown-item ${metricsTimePreset === preset.value ? 'active' : ''}`}
                      onClick={() => { setMetricsTimePreset(preset.value); if (preset.value !== 'custom') setShowMetricsTimePresets(false); }}
                    >
                      {preset.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-spacer"></div>

            {metricsTimeRange && (
              <span className="time-range-label">
                {new Date(metricsTimeRange.startTime).toLocaleString()} — {new Date(metricsTimeRange.endTime).toLocaleString()}
                <span className="alignment-info">({metricsTimeRange.alignmentPeriodSeconds}s alignment)</span>
              </span>
            )}
          </div>

          {metricsTimePreset === 'custom' && (
            <div className="custom-time-range">
              <div className="time-input-group">
                <label>Start Time</label>
                <input
                  type="datetime-local"
                  value={metricsStartTime ? toLocalDatetimeString(metricsStartTime) : ''}
                  onChange={(e) => setMetricsStartTime(e.target.value ? new Date(e.target.value).toISOString() : '')}
                  max={toLocalDatetimeString(new Date().toISOString())}
                />
              </div>
              <div className="time-input-group">
                <label>End Time</label>
                <input
                  type="datetime-local"
                  value={metricsEndTime ? toLocalDatetimeString(metricsEndTime) : ''}
                  onChange={(e) => setMetricsEndTime(e.target.value ? new Date(e.target.value).toISOString() : '')}
                  max={toLocalDatetimeString(new Date().toISOString())}
                />
              </div>
              <button className="btn btn-primary btn-sm" onClick={handleMetricsCustomApply}>
                Apply
              </button>
            </div>
          )}

          {!metrics || Object.keys(metrics).length === 0 ? (
            <div className="empty-state">
              <h3>No metrics available</h3>
              <p>No metrics data found for the selected time range</p>
            </div>
          ) : (
            <div className="metrics-grid">
              {Object.entries(metrics).map(([name, data]) => (
                <MetricCard key={name} name={name} data={data} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function safeISOString(val) {
  if (!val) return '';
  try {
    const d = new Date(typeof val === 'object' && val.seconds ? val.seconds * 1000 : val);
    return isNaN(d.getTime()) ? String(val) : d.toISOString();
  } catch { return String(val); }
}

function LogKV({ label, value }) {
  if (!value && value !== 0) return null;
  const strVal = String(value);
  return (
    <div className="log-kv-row">
      <span className="log-kv-key">{label}</span>
      <span className="log-kv-val">{strVal}</span>
    </div>
  );
}

function LogRow({ log, getSeverityClass }) {
  const [expanded, setExpanded] = useState(false);

  const content = log.textPayload || (log.jsonPayload ? JSON.stringify(log.jsonPayload) : '');
  const shortContent = content.length > 200 ? content.slice(0, 200) + '...' : content;

  const hasResourceLabels = log.resource?.labels && Object.keys(log.resource.labels).length > 0;
  const hasLabels = log.labels && Object.keys(log.labels).length > 0;
  const hasHttp = log.httpRequest && (log.httpRequest.requestMethod || log.httpRequest.requestUrl);
  const hasOperation = log.operation && (log.operation.id || log.operation.producer);
  const hasSource = log.sourceLocation && (log.sourceLocation.file || log.sourceLocation.function);

  return (
    <div className={`log-row ${getSeverityClass(log.severity)} ${expanded ? 'expanded' : ''}`}>
      <div className="log-row-main" onClick={() => setExpanded(!expanded)}>
        <div className="log-col-chevron"><span className={`log-chevron ${expanded ? 'open' : ''}`}>&#9654;</span></div>
        <div className="log-col-severity">
          <span className={`log-severity-badge ${getSeverityClass(log.severity)}`}>
            {log.severity}
          </span>
        </div>
        <div className="log-col-timestamp">
          {new Date(log.timestamp).toLocaleString()}
        </div>
        <div className="log-col-content">
          {expanded ? content : shortContent}
        </div>
      </div>
      {expanded && (
        <div className="log-row-expanded" onClick={e => e.stopPropagation()}>
          {log.jsonPayload && (
            <div className="log-exp-section">
              <div className="log-exp-title">JSON Payload</div>
              <div className="log-json-view"><pre>{JSON.stringify(log.jsonPayload, null, 2)}</pre></div>
            </div>
          )}
          {log.textPayload && (
            <div className="log-exp-section">
              <div className="log-exp-title">Text Payload</div>
              <div className="log-text-view">{log.textPayload}</div>
            </div>
          )}

          <div className="log-exp-section">
            <div className="log-exp-title">Log Entry</div>
            <div className="log-kv-table">
              <LogKV label="Insert ID" value={log.insertId} />
              <LogKV label="Log Name" value={log.logName} />
              <LogKV label="Severity" value={log.severity} />
              <LogKV label="Timestamp" value={safeISOString(log.timestamp)} />
              <LogKV label="Receive Timestamp" value={safeISOString(log.receiveTimestamp)} />
              <LogKV label="Trace" value={log.trace} />
              <LogKV label="Span ID" value={log.spanId} />
              <LogKV label="Trace Sampled" value={log.traceSampled ? 'true' : log.spanId ? 'false' : ''} />
            </div>
          </div>

          {log.resource && (
            <div className="log-exp-section">
              <div className="log-exp-title">Resource</div>
              <div className="log-kv-table">
                <LogKV label="Type" value={log.resource.type} />
                {hasResourceLabels && Object.entries(log.resource.labels).map(([k, v]) => (
                  <LogKV key={k} label={k} value={v} />
                ))}
              </div>
            </div>
          )}

          {hasLabels && (
            <div className="log-exp-section">
              <div className="log-exp-title">Labels</div>
              <div className="log-kv-table">
                {Object.entries(log.labels).map(([k, v]) => (
                  <LogKV key={k} label={k} value={v} />
                ))}
              </div>
            </div>
          )}

          {hasHttp && (
            <div className="log-exp-section">
              <div className="log-exp-title">HTTP Request</div>
              <div className="log-kv-table">
                <LogKV label="Method" value={log.httpRequest.requestMethod} />
                <LogKV label="URL" value={log.httpRequest.requestUrl} />
                <LogKV label="Status" value={log.httpRequest.status} />
                <LogKV label="Response Size" value={log.httpRequest.responseSize} />
                <LogKV label="Request Size" value={log.httpRequest.requestSize} />
                <LogKV label="User Agent" value={log.httpRequest.userAgent} />
                <LogKV label="Remote IP" value={log.httpRequest.remoteIp} />
                <LogKV label="Server IP" value={log.httpRequest.serverIp} />
                <LogKV label="Latency" value={log.httpRequest.latency} />
                <LogKV label="Protocol" value={log.httpRequest.protocol} />
                <LogKV label="Referer" value={log.httpRequest.referer} />
              </div>
            </div>
          )}

          {hasOperation && (
            <div className="log-exp-section">
              <div className="log-exp-title">Operation</div>
              <div className="log-kv-table">
                <LogKV label="ID" value={log.operation.id} />
                <LogKV label="Producer" value={log.operation.producer} />
                <LogKV label="First" value={log.operation.first ? 'true' : ''} />
                <LogKV label="Last" value={log.operation.last ? 'true' : ''} />
              </div>
            </div>
          )}

          {hasSource && (
            <div className="log-exp-section">
              <div className="log-exp-title">Source Location</div>
              <div className="log-kv-table">
                <LogKV label="File" value={log.sourceLocation.file} />
                <LogKV label="Line" value={log.sourceLocation.line} />
                <LogKV label="Function" value={log.sourceLocation.function} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const METRIC_COLORS = {
  'Request Count': { stroke: '#3b82f6', gradient: ['rgba(59,130,246,0.3)', 'rgba(59,130,246,0.02)'] },
  'Request Latencies (P95)': { stroke: '#f59e0b', gradient: ['rgba(245,158,11,0.3)', 'rgba(245,158,11,0.02)'] },
  'CPU Utilization': { stroke: '#10b981', gradient: ['rgba(16,185,129,0.3)', 'rgba(16,185,129,0.02)'] },
  'Memory Utilization': { stroke: '#8b5cf6', gradient: ['rgba(139,92,246,0.3)', 'rgba(139,92,246,0.02)'] },
  'Instance Count': { stroke: '#06b6d4', gradient: ['rgba(6,182,212,0.3)', 'rgba(6,182,212,0.02)'] },
  'Billable Instance Time': { stroke: '#ef4444', gradient: ['rgba(239,68,68,0.3)', 'rgba(239,68,68,0.02)'] },
};

function MetricCard({ name, data }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [chartType, setChartType] = useState('bar');
  const colors = METRIC_COLORS[name] || METRIC_COLORS['Request Count'];
  const gradientId = `grad-crd-${name.replace(/[^a-zA-Z]/g, '')}`;

  if (!data || data.totalDataPoints === 0) {
    return (
      <div className="card metric-card">
        <div className="mc-header"><h3>{name}</h3>{data?.unit && <span className="mc-unit">{data.unit}</span>}</div>
        <div className="metric-empty">No data points in this time range</div>
      </div>
    );
  }

  const points = data.points || [];
  const values = points.map(p => p.value).filter(v => v !== null && v !== undefined);
  const maxVal = Math.max(...values, 1e-9);
  const minVal = Math.min(...values);
  const avgVal = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  const latestVal = values.length > 0 ? values[values.length - 1] : 0;

  const fmtVal = (v) => {
    if (data.unit === '%') return (v * 100).toFixed(1) + '%';
    if (data.unit === 'ms') return v.toFixed(1) + ' ms';
    if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + 'M';
    if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + 'K';
    if (Number.isInteger(v)) return String(v);
    return v.toFixed(2);
  };

  const step = Math.max(1, Math.floor(points.length / 80));
  const sampled = [];
  for (let i = 0; i < points.length; i += step) sampled.push(points[i]);
  if (sampled.length === 0) sampled.push(...points.slice(0, 1));

  const W = 400, H = 100, PAD = 2;
  const chartW = W - PAD * 2, chartH = H - PAD * 2;
  const range = maxVal - minVal || 1;

  const pathPoints = sampled.map((p, i) => ({
    x: PAD + (i / Math.max(sampled.length - 1, 1)) * chartW,
    y: PAD + chartH - ((p.value - minVal) / range) * chartH,
  }));
  const linePath = pathPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L${pathPoints[pathPoints.length - 1].x.toFixed(1)},${H} L${pathPoints[0].x.toFixed(1)},${H} Z`;

  const barWidth = Math.max(1, (chartW / sampled.length) * 0.7);
  const barGap = (chartW / sampled.length) * 0.15;

  const hoverPt = hoverIndex !== null && hoverIndex < sampled.length ? sampled[hoverIndex] : null;

  return (
    <div className="card metric-card" style={{ borderTop: `2px solid ${colors.stroke}` }}>
      <div className="mc-header">
        <h3>{name}</h3>
        <div className="mc-header-right">
          {data.unit && <span className="mc-unit">{data.unit}</span>}
          <div className="mc-chart-toggle">
            <button className={`mc-toggle-btn ${chartType === 'bar' ? 'active' : ''}`} onClick={() => setChartType('bar')} title="Bar Chart">
              <svg width="14" height="14" viewBox="0 0 14 14"><rect x="1" y="6" width="3" height="7" rx="0.5" fill="currentColor"/><rect x="5.5" y="3" width="3" height="10" rx="0.5" fill="currentColor"/><rect x="10" y="1" width="3" height="12" rx="0.5" fill="currentColor"/></svg>
            </button>
            <button className={`mc-toggle-btn ${chartType === 'line' ? 'active' : ''}`} onClick={() => setChartType('line')} title="Line Chart">
              <svg width="14" height="14" viewBox="0 0 14 14"><polyline points="1,11 4,6 7,8 10,3 13,5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>

      <div className="mc-stats">
        <div className="mc-stat"><span className="mc-stat-val" style={{ color: colors.stroke }}>{fmtVal(latestVal)}</span><span className="mc-stat-lbl">Latest</span></div>
        <div className="mc-stat"><span className="mc-stat-val">{fmtVal(avgVal)}</span><span className="mc-stat-lbl">Avg</span></div>
        <div className="mc-stat"><span className="mc-stat-val">{fmtVal(maxVal)}</span><span className="mc-stat-lbl">Max</span></div>
        <div className="mc-stat"><span className="mc-stat-val">{fmtVal(minVal)}</span><span className="mc-stat-lbl">Min</span></div>
      </div>

      {hoverPt && <div className="mc-tooltip"><span className="mc-tooltip-val" style={{ color: colors.stroke }}>{fmtVal(hoverPt.value)}</span><span className="mc-tooltip-ts">{new Date(hoverPt.timestamp).toLocaleString()}</span></div>}

      <div className="mc-chart-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="mc-svg">
          <defs><linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={colors.gradient[0]} /><stop offset="100%" stopColor={colors.gradient[1]} /></linearGradient></defs>
          {[0.25, 0.5, 0.75].map(pct => <line key={pct} x1={PAD} x2={W - PAD} y1={PAD + chartH * (1 - pct)} y2={PAD + chartH * (1 - pct)} stroke="var(--chart-grid)" strokeWidth="0.5" />)}

          {chartType === 'bar' ? (
            <>
              {sampled.map((p, i) => {
                const barX = PAD + (i / Math.max(sampled.length, 1)) * chartW + barGap;
                const barH = ((p.value - minVal) / range) * chartH;
                const barY = PAD + chartH - barH;
                return (
                  <g key={i} onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)}>
                    <rect x={barX} y={barY} width={barWidth} height={Math.max(barH, 0.5)} rx="0.5"
                      fill={hoverIndex === i ? colors.stroke : `url(#${gradientId})`}
                      stroke={colors.stroke} strokeWidth="0.3" opacity={hoverIndex === i ? 1 : 0.85} />
                    <rect x={barX} y={0} width={barWidth} height={H} fill="transparent" />
                  </g>
                );
              })}
            </>
          ) : (
            <>
              <path d={areaPath} fill={`url(#${gradientId})`} />
              <path d={linePath} fill="none" stroke={colors.stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              {pathPoints.map((p, i) => <rect key={i} x={p.x - chartW / sampled.length / 2} y={0} width={chartW / sampled.length} height={H} fill="transparent" onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)} />)}
              {hoverIndex !== null && pathPoints[hoverIndex] && (
                <><line x1={pathPoints[hoverIndex].x} x2={pathPoints[hoverIndex].x} y1={PAD} y2={H} stroke="var(--alpha-white-15)" strokeWidth="0.5" strokeDasharray="3,2" /><circle cx={pathPoints[hoverIndex].x} cy={pathPoints[hoverIndex].y} r="3" fill={colors.stroke} stroke="var(--bg-primary)" strokeWidth="1.5" /></>
              )}
            </>
          )}
        </svg>
      </div>

      <div className="mc-footer">
        <span>{points.length > 0 ? new Date(points[0].timestamp).toLocaleString() : ''}</span>
        <span>{data.totalDataPoints} pts</span>
        <span>{points.length > 0 ? new Date(points[points.length - 1].timestamp).toLocaleString() : ''}</span>
      </div>
    </div>
  );
}

function InfoItem({ label, value, link }) {
  return (
    <div className="info-item">
      <span className="info-label">{label}</span>
      {link && value ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="info-value link">{value}</a>
      ) : (
        <span className="info-value">{value || 'N/A'}</span>
      )}
    </div>
  );
}

function formatDate(dateValue) {
  if (!dateValue) return 'N/A';
  try {
    if (dateValue.seconds) return new Date(dateValue.seconds * 1000).toLocaleString();
    return new Date(dateValue).toLocaleString();
  } catch {
    return String(dateValue);
  }
}

export default CloudRunDetail;
